import React from 'react';
import { BarList, fmtDate } from '../../kit';
import { DesignDoc, forWire } from '../designs/types';
import {
  LIFECYCLE_LABEL, TagFilterValue, describeTagFilter, tagFilterFromParams, tagFilterParams,
  tagText, toLifecycle,
} from '../../tags/shared';

/**
 * Vocabulary and small pieces the campaign views share (backend:
 * Backend/admin_email_targeting.py — target_list / target_preview /
 * target_send / target_sends / target_funnel / tag_funnel / senders).
 */

export const DEFAULT_CAP = 2000;
// A GET carries its params in the URL; past this the request line is longer
// than a proxy will accept, so the page says so instead of failing with a 414.
export const MAX_QUERY = 7000;
export const SAMPLE_COUNT = 3;

// admin_email_targeting.EXCLUSION_ORDER — each person is counted once, under
// the first reason that applies, so these add up to matched − count.
export const EXCLUDED_LABEL: Record<string, string> = {
  no_email: 'No e-mail',
  inactive: 'Inactive account',
  deletion_requested: 'Asked to delete account',
  bounced: 'Bounced / blocked / spam report',
  team: 'Our team',
  already_received: 'Already received it',
  unticked: 'Unticked by you',
  unsubscribed: 'Unsubscribed (MEMAC link)',
};

export const num = (v: any) => Number(v ?? 0).toLocaleString();
export const labelize = (k: string) => k.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

/** "3 days ago" for the last-e-mailed note beside each person. */
export const ago = (iso?: string | null): string => {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const ms = Date.now() - t;
  const days = Math.floor(ms / 86400000);
  if (days <= 0) {
    const h = Math.floor(ms / 3600000);
    return h <= 0 ? 'just now' : `${h} h ago`;
  }
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

export const parseEmails = (s: string) =>
  Array.from(new Set(s.split(/[\s,;]+/).map(x => x.trim().replace(/^<|>$/g, '').toLowerCase())
    .filter(x => x.includes('@'))));

// Same strictness as email_templates._EMAIL_RE, so what passes here passes there.
export const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// ---------------------------------------------------------------------------
// Audience
// ---------------------------------------------------------------------------
export type Team = 'exclude' | 'include' | 'only';
/** The filters beyond the shared TagFilter that this tab always had. */
export type Extra = {
  countries: string[];
  organisation: string;
  designation: string;
  q: string;
  min_searches: string; max_searches: string;
  team: Team;
  trial_started_from: string; trial_started_to: string;
  emails: string;
  received_template: string[];
  not_received_template: string[];
};
export const EMPTY_EXTRA: Extra = {
  countries: [], organisation: '', designation: '', q: '', min_searches: '', max_searches: '',
  team: 'exclude', trial_started_from: '', trial_started_to: '', emails: '',
  received_template: [], not_received_template: [],
};

/**
 * The group filters exactly as admin_email_targeting.parse_filters reads
 * them: the shared tag filter's params (user_tags vocabulary) plus this tab's
 * extras, only what is set. One function builds the list, preview and send
 * requests, so all three describe the same people.
 */
export const audienceWire = (tf: TagFilterValue, x: Extra): Record<string, any> => {
  const out: Record<string, any> = { ...tagFilterParams(tf) };
  if (x.countries.length) out.countries = x.countries;
  if (x.organisation.trim()) out.organisation = x.organisation.trim();
  if (x.designation.trim()) out.designation = x.designation.trim();
  if (x.q.trim()) out.q = x.q.trim();
  const n = (s: string) => (s.trim() !== '' && Number.isFinite(Number(s)) && Number(s) >= 0)
    ? Math.floor(Number(s)) : null;
  if (n(x.min_searches) !== null) out.min_searches = n(x.min_searches);
  if (n(x.max_searches) !== null) out.max_searches = n(x.max_searches);
  if (x.team !== 'exclude') out.team = x.team;
  if (x.trial_started_from) out.trial_started_from = x.trial_started_from;
  if (x.trial_started_to) out.trial_started_to = x.trial_started_to;
  const emails = parseEmails(x.emails);
  if (emails.length) out.emails = emails;
  if (x.received_template.length) out.received_template = x.received_template;
  if (x.not_received_template.length) out.not_received_template = x.not_received_template;
  return out;
};

/**
 * A batch's stored filters echo (parse_filters' normalised keys) in words.
 * Covers the older keys too (states, signup_*, last_active_*, never_active),
 * because campaigns sent before the shared tag filter are in the history.
 */
export const describeEcho = (echo: Record<string, any> | null | undefined,
                             tagLabel: (k: string) => string, tplName: (k: string) => string): string[] => {
  const e = echo || {};
  // tagFilterFromParams reads query-string shapes: lists comma-joined, flags '1' / '0'.
  const asParams: Record<string, string> = {};
  Object.entries(e).forEach(([k, v]) => {
    asParams[k] = Array.isArray(v) ? v.join(',') : typeof v === 'boolean' ? (v ? '1' : '0') : String(v ?? '');
  });
  const out = describeTagFilter(tagFilterFromParams(asParams), tagLabel);
  const list = (k: string): string[] => (Array.isArray(e[k]) ? e[k].map(String) : []);
  const range = (label: string, a?: string, b?: string) => { if (a || b) out.push(`${label}: ${a || '…'} → ${b || '…'}`); };
  if (list('states').length && !list('lifecycle').length) {
    out.push(`Billing state: ${list('states').map(s => LIFECYCLE_LABEL[toLifecycle(s)] || s).join(', ')}`);
  }
  if (list('countries').length) out.push(`Country: ${list('countries').join(', ')}`);
  range('Signed up', e.signup_from, e.signup_to);
  range('Trial started', e.trial_started_from, e.trial_started_to);
  range('Last active', e.last_active_from, e.last_active_to);
  if (e.never_active) out.push('Never active');
  if (e.organisation) out.push(`Organisation contains “${e.organisation}”`);
  if (e.designation) out.push(`Designation contains “${e.designation}”`);
  if (e.q) out.push(`Name / e-mail contains “${e.q}”`);
  if (e.min_searches !== undefined) out.push(`At least ${e.min_searches} searches`);
  if (e.max_searches !== undefined) out.push(`At most ${e.max_searches} searches`);
  if (list('emails').length) out.push(`Only ${num(list('emails').length)} pasted address(es)`);
  if (list('user_ids').length) out.push(`${num(list('user_ids').length)} people picked by hand`);
  if (list('received_template').length) out.push(`Has received ${list('received_template').map(tplName).join(' or ')}`);
  if (list('not_received_template').length) out.push(`Has not received ${list('not_received_template').map(tplName).join(' or ')}`);
  if (list('exclude_user_ids').length) out.push(`${num(list('exclude_user_ids').length)} unticked by hand`);
  if (e.team === 'include') out.push('Team included');
  if (e.team === 'only') out.push('Team only');
  return out.length ? out : ['Everyone (no filter)'];
};

/** The tab's own extra filters in words (the tag filter's come from describeTagFilter). */
export const describeExtra = (x: Extra, tplName: (k: string) => string): string[] => {
  const out: string[] = [];
  if (x.countries.length) out.push(`Country: ${x.countries.join(', ')}`);
  if (x.trial_started_from || x.trial_started_to) {
    out.push(`Trial started: ${x.trial_started_from || '…'} → ${x.trial_started_to || '…'}`);
  }
  if (x.organisation.trim()) out.push(`Organisation contains “${x.organisation.trim()}”`);
  if (x.designation.trim()) out.push(`Designation contains “${x.designation.trim()}”`);
  if (x.q.trim()) out.push(`Name / e-mail contains “${x.q.trim()}”`);
  if (x.min_searches.trim()) out.push(`At least ${x.min_searches.trim()} searches`);
  if (x.max_searches.trim()) out.push(`At most ${x.max_searches.trim()} searches`);
  const n = parseEmails(x.emails).length;
  if (n) out.push(`Only these ${n} address${n === 1 ? '' : 'es'}`);
  if (x.received_template.length) out.push(`Has received ${x.received_template.map(tplName).join(' or ')}`);
  if (x.not_received_template.length) out.push(`Has not received ${x.not_received_template.map(tplName).join(' or ')}`);
  out.push(x.team === 'include' ? 'Team included' : x.team === 'only' ? 'Team only' : 'Team left out');
  return out;
};

// ---------------------------------------------------------------------------
// People rows (target_list / target_people_search)
// ---------------------------------------------------------------------------
export type Person = {
  user_id: string; email: string; name?: string; org?: string; designation?: string; country?: string;
  lifecycle?: string | null; subtag?: string | null; persona?: string | null; persona_label?: string | null;
  tags?: any[]; auto_tags?: any[]; last_emailed_at?: string | null; signed_up_at?: string | null;
  last_active?: string | null; searches?: number; reason?: string; detail?: string; is_active?: boolean;
};

/** The "last e-mailed" cell: a note, never a reason to leave someone out. */
export const LastEmailed: React.FC<{ at?: string | null }> = ({ at }) => (
  <span title={at ? fmtDate(at) : 'No e-mail from us on record'}
    className={at && Date.now() - Date.parse(at) < 7 * 86400000 ? 'text-amber-800' : 'text-grayscale-1100'}>
    {at ? `e-mailed ${ago(at)}` : 'never e-mailed'}
  </span>
);

/** Every row as CSV, whatever the table on screen is filtered to. */
export const downloadPeopleCsv = (name: string, rows: Array<Person & { ticked?: boolean }>) => {
  const cols: Array<[string, (r: Person & { ticked?: boolean }) => any]> = [
    ['included', r => (r.reason ? `no — ${EXCLUDED_LABEL[r.reason] || r.reason}` : r.ticked === false ? 'no — unticked' : 'yes')],
    ['email', r => r.email], ['name', r => r.name], ['organisation', r => r.org],
    ['designation', r => r.designation], ['country', r => r.country],
    ['lifecycle', r => LIFECYCLE_LABEL[toLifecycle(r.lifecycle)] || r.lifecycle || ''],
    ['persona', r => r.persona_label || r.persona || ''], ['tags', r => tagText(r)],
    ['last_emailed_at', r => r.last_emailed_at || ''], ['signed_up_at', r => r.signed_up_at || ''],
    ['user_id', r => r.user_id],
  ];
  const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.map(c => esc(c[0])).join(',')]
    .concat(rows.map(r => cols.map(([, f]) => esc(f(r))).join(','))).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
export type Content = {
  /** template: an editable template · custom: written here (rich text) ·
   *  design: built with the e-mail design builder (email_designs). */
  mode: 'template' | 'custom' | 'design';
  key: string;
  subject: string;
  /** HTML, or plain text with blank-line paragraphs. */
  body: string;
  /** Optional plain-text part; the server generates it from the HTML when blank. */
  text: string;
  name: string;
  from_email: string;
  from_name: string;
  reply_to: string;
  cc: string;
  bcc: string;
  /** Design mode: the design being edited (client keys included). */
  design: DesignDoc | null;
  /** Which library design it started from (shown as "Started from …"). */
  design_from: string;
  /** What a placeholder becomes for whoever has no value, in this campaign
   *  ('' = left blank); a placeholder not here keeps its default. */
  fallbacks: Record<string, string>;
  /** Site-wide values (site_url, upgrade_url, the counts) for this campaign. */
  overrides: Record<string, string>;
};
export const EMPTY_CONTENT: Content = {
  mode: 'template', key: '', subject: '', body: '', text: '', name: '',
  from_email: '', from_name: '', reply_to: '', cc: '', bcc: '', design: null, design_from: '',
  fallbacks: {}, overrides: {},
};
export type Sender = { email: string; name: string };

/** The content + envelope as parse_content reads them. */
export const contentWire = (c: Content, skipReceived: boolean): Record<string, any> => ({
  content_mode: c.mode,
  ...(c.mode === 'template'
    ? { key: c.key, skip_already_received: skipReceived }
    : c.mode === 'design'
      // No text part: a design's plain text is always made from the design.
      ? { subject: c.subject, design: c.design ? forWire(c.design) : null, text: '' }
      : { subject: c.subject, body: c.body, text: c.text }),
  name: c.name.trim(),
  from_email: c.from_email,
  from_name: c.from_name.trim(),
  reply_to: c.reply_to.trim(),
  cc: parseEmails(c.cc),
  bcc: parseEmails(c.bcc),
  placeholder_fallbacks: c.fallbacks,
  placeholder_overrides: c.overrides,
});

/** What would stop a preview, in words ('' when none). */
export const contentProblem = (c: Content, maxCc: number): string => {
  if (c.mode === 'template' && !c.key) return 'Pick a template.';
  if (c.mode !== 'template' && !c.subject.trim()) return 'Write a subject.';
  if (c.mode === 'custom' && !c.body.trim()) return 'Write the e-mail body.';
  if (c.mode === 'design' && !c.design?.blocks?.length) return 'Pick a design.';
  for (const [label, v] of [['CC', c.cc], ['BCC', c.bcc]] as const) {
    const list = parseEmails(v);
    const bad = list.filter(e => !EMAIL_RE.test(e));
    if (bad.length) return `${label}: not an e-mail address: ${bad.slice(0, 3).join(', ')}`;
    if (list.length > maxCc) return `${label}: at most ${maxCc} addresses.`;
    if (v.trim() && !list.length) return `${label}: no address recognised.`;
  }
  if (c.reply_to.trim()) {
    const m = c.reply_to.trim().match(/<\s*([^<>\s]+)\s*>$/);
    if (!EMAIL_RE.test(m ? m[1] : c.reply_to.trim())) return 'Reply-To must be an e-mail address, or Name <address>.';
  }
  return '';
};

// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------
export type Step = { key: string; label: string; n: number; pct: number };

const SENTENCE: Record<string, string> = {
  sent: 'sent', opened: 'opened', clicked: 'clicked', visited: 'visited',
  signed_up: 'signed up', trial: 'started a trial',
};

/** "500 sent → 210 opened → 60 clicked → 45 visited → 12 signed up → 4 started a trial".
 *  With `words`, a funnel of other steps (Email Center → Invites: sent →
 *  delivered → opened → clicked → signed in) reads its own steps, in order. */
export const funnelSentence = (steps: Step[] | Record<string, number> | null | undefined,
                               words?: Record<string, string>): string => {
  if (!steps) return '';
  if (words && Array.isArray(steps)) {
    return steps.map(s => `${num(s.n)} ${words[s.key] ?? s.label.toLowerCase()}`).join(' → ');
  }
  const n = (k: string) => Array.isArray(steps) ? (steps.find(s => s.key === k)?.n ?? 0) : Number(steps[k] ?? 0);
  return Object.keys(SENTENCE).map(k => `${num(n(k))} ${SENTENCE[k]}`).join(' → ');
};

/** The steps as bars, each with its share of those sent. */
export const FunnelSteps: React.FC<{
  steps: Step[]; head?: { label: string; n: number }; words?: Record<string, string>;
}> = ({ steps, head, words }) => {
  const sent = steps.find(s => s.key === 'sent')?.n ?? 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium text-grayscale-1300">
        {head && <>{num(head.n)} {head.label} → </>}{funnelSentence(steps, words)}
      </div>
      {sent > 0 ? (
        <BarList items={steps.map(s => ({ label: s.label, value: s.n }))} total={sent} />
      ) : (
        <div className="text-xs text-grayscale-1100">Nothing sent yet, so there is no funnel.</div>
      )}
    </div>
  );
};

/** How long after a send a sign-up or trial still counts (backend attr_days). */
export const ATTR_OPTIONS = ['7', '30', '90', '0'];
export const ATTR_LABELS: Record<string, string> = {
  '7': 'within 7 days', '30': 'within 30 days', '90': 'within 90 days', '0': 'any time after',
};
